import {
  Box,
  Typography,
  CircularProgress,
  Snackbar,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
  TextField,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import SidePanel from "../../components/SidePanel";
import TopBar from "../../components/TopBar";
import "./style.css";
import { formatTime } from "../../utils/util";
import { getReferredUsersById, getUsersMetReferralPlans, sendPromoCode } from "../../apis/referralPlans";
import { getUserReferralTracking } from "../../actions/referralAction";

const ReferralTracker = () => {
  const dispatch = useDispatch();
  const { referralTrackData } = useSelector((state: any) => state.referralPlansReducer);

  // to handle the local state of the components
  const [referralDetails, setReferralDetails] = useState(referralTrackData);
  const [filteredReferralDetails, setFilteredReferralDetails] = useState(referralTrackData);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");
  const [open, setOpen] = useState(false);
// State for popup
  const [popupOpen, setPopupOpen] = useState(false);
  const [referredUsers, setReferredUsers] = useState<any[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [referredLoading, setReferredLoading] = useState(false);
  const fetchReferredUsers = async (userId: string) => {
    setReferredLoading(true);
    try {
      const response: any = await getReferredUsersById(userId);
      if (!response) {
        setReferredLoading(false);
        return;
      }
      // Check if the response is valid
    
      if (response.status) {
        setReferredUsers(response.data.referred_users);
      } else {
        setReferredUsers([]);
      }
    } catch (error) {
      console.error("Error fetching referred users:", error);
      setReferredUsers([]);
    } finally {
      setReferredLoading(false);
    }
  };
  const handleReferralClick = (userId: string) => {
    setSelectedUserId(userId);
    setPopupOpen(true);
    fetchReferredUsers(userId);
  };

  const handleClosePopup = () => {
    setPopupOpen(false);
    setReferredUsers([]);
    setSelectedUserId(null);
  };
  
  // Function to fetch all the users data met with the referral plan and update
  const getUsersData = async () => {
    setLoading(true);
    const data = await getUsersMetReferralPlans();
    dispatch(getUserReferralTracking(data));
    setReferralDetails(data);
    setFilteredReferralDetails(data);
    setLoading(false);
  };

  // Function to handle search
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const searchTerm = event.target.value.toLowerCase();
    setSearch(event.target.value);
    
    if (searchTerm === "") {
      setFilteredReferralDetails(referralDetails);
    } else {
      const filtered = referralDetails.filter((row: any) => {
        const fullName = `${row.first_name} ${row.last_name}`.toLowerCase();
        return fullName.includes(searchTerm);
      });
      setFilteredReferralDetails(filtered);
    }
  };

  // methods to handle the snackbar rendering
  const openSnackBar = () => {
    setOpen(true);
    window.location.reload();
  };
  const closeSnackBar = (event: React.SyntheticEvent | Event, reason?: string) => {
    setOpen(false);
  };

  // Method to handle mark promo sent action
  const onSendPromo = async (userId: string) => {
    let res = await sendPromoCode(userId);
    if (res?.status) {
      setStatusMessage(res?.data);
      openSnackBar();
    }
  };

  useEffect(() => {
    //fetch all the users met with referrals plans
    getUsersData();
  }, []);

  // Update filtered list when referralDetails changes
  useEffect(() => {
    if (search === "") {
      setFilteredReferralDetails(referralDetails);
    } else {
      const searchTerm = search.toLowerCase();
      const filtered = referralDetails.filter((row: any) => {
        const fullName = `${row.first_name} ${row.last_name}`.toLowerCase();
        return fullName.includes(searchTerm);
      });
      setFilteredReferralDetails(filtered);
    }
  }, [referralDetails, search]);

  return (
    <Box className="mainContainer">
      <Box className="sidePanelContainer">
        <SidePanel isOpen={true} onClose={() => {}} />
      </Box>
      <Box className="dashboardContainer">
        <TopBar />
        <Box style={{ width: "100%", padding: "20px" }}>
          {loading ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginTop: "200px",
              }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <Box style={{ marginTop: "100px" }}>
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: "20px",
                }}
              >
                <Typography style={{ fontWeight: 700, fontSize: 20 }}>
                  Users Met with Referrals Plans
                </Typography>
              </div>

              <Box sx={{ marginBottom: 2 }}>
                <TextField
                  label="Search by User Name"
                  variant="outlined"
                  size="small"
                  value={search}
                  onChange={handleSearchChange}
                  fullWidth
                  sx={{ maxWidth: 400 }}
                />
              </Box>

              <TableContainer component={Paper}>
                <Table sx={{ minWidth: 650 }} aria-label="simple table">
                  <TableHead style={{ backgroundColor: "rgb(224, 224, 224)" }}>
                    <TableRow>
                      <TableCell className="cellContainer">User Name</TableCell>
                      <TableCell className="cellContainer">Email</TableCell>
                      <TableCell className="cellContainer">Referral Plan</TableCell>
                      <TableCell className="cellContainer">Referred Count</TableCell>
                      <TableCell className="cellContainer">Promo Code</TableCell>
                      <TableCell className="cellContainer">Promo Sent</TableCell>
                      <TableCell align="center" className="cellContainer">
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredReferralDetails &&
                      filteredReferralDetails.map((row: any) => (
                        <TableRow
                          key={row._id}
                          sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                        >
                          <TableCell
                            component="th"
                            scope="row"
                            style={{ textTransform: "capitalize" }}
                          >
                            {row.first_name} {row.last_name}
                          </TableCell>
                          <TableCell>{row.email}</TableCell>
                          <TableCell>{row.referral_plan_name}</TableCell>
                         
                          <TableCell
                          data-referral-cell="true"
                          style={{
                            cursor:
                              row.referral_plan_count && row.referral_plan_count > 0
                                ? "pointer"
                                : "default",
                            color:
                              row.referral_plan_count && row.referral_plan_count > 0 ? "blue" : "black",
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (row.referral_plan_count && row.referral_plan_count > 0) {
                              handleReferralClick(row.referrer_user_id);
                            }
                          }}
                        >
                          {row.referral_plan_count || 0}
                        </TableCell>
                          <TableCell>{row.promo_code}</TableCell>
                          <TableCell>{row.promo_sent ? "Yes" : "No"}</TableCell>
                          <TableCell align="center">
                            <Button
                              variant="contained"
                              disabled={!!row.promo_sent}
                              onClick={() => onSendPromo(row._id)}
                            >
                              Mark Promo Sent
                            </Button>
                            <Snackbar
                              anchorOrigin={{ vertical: "top", horizontal: "right" }}
                              open={open}
                              autoHideDuration={3000}
                              onClose={closeSnackBar}
                            >
                              <Alert
                                onClose={closeSnackBar}
                                severity="success"
                                sx={{ width: "100%", alignItems: "center" }}
                              >
                                {statusMessage}
                              </Alert>
                            </Snackbar>
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </Box>
      </Box>
       {/* Popup for Referred Users */}
       <Dialog open={popupOpen} onClose={handleClosePopup} fullWidth maxWidth="sm">
        <DialogTitle>Referred Users</DialogTitle>
        <DialogContent>
          {referredLoading ? (
            <CircularProgress />
          ) : referredUsers.length > 0 ? (
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>User Type</TableCell>
                  <TableCell>Phone Number</TableCell>
                  <TableCell>Total Rides</TableCell>
                  <TableCell>Rejected Rides</TableCell>
                  <TableCell>Referral Date</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {referredUsers.map((user) => (
                  <TableRow key={user.referred_user_id}>
                    <TableCell>{user.referred_user_name}</TableCell>
                    <TableCell>{user.user_type}</TableCell>
                    <TableCell>{user.phone_number}</TableCell>
                    <TableCell>{user.total_ride_count}</TableCell>
                    <TableCell>{user.total_rejected_ride_count}</TableCell>
                    <TableCell>{formatTime(user.referral_date)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table> 
          ) : (
            <p>No referred users found.</p>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePopup}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ReferralTracker;
